import React, { Fragment, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { utils, siteLocales, linkEditing, site } from '@yola/ws-sdk';
import { designSystem, reactHookForm } from '@yola/ws-ui';
import PageSelector from './page-selector';
import constants from '../constants';

const { useFormContext, useWatch } = reactHookForm;

const {
  linkSettings: { INPUTS_IDS, FIELD_NAMES },
} = constants;

const { linkTypes } = linkEditing.constants;
const { Stack, InputGroupField, FilePickerField, SearchableSelectField, SimpleSelectField } =
  designSystem;

const findOptionByValue = (value, options) => {
  const isGroupOptions = Boolean(options[0]?.options);
  let allOptions = options;

  if (isGroupOptions) {
    allOptions = options.reduce(
      (resultArray, currentOption) => resultArray.concat(currentOption.options),
      []
    );
  }

  return allOptions.find((option) => option.value === value);
};

function LinkSelectorField({
  latestAnchorReference,
  options,
  captions,
  onChange,
  onFileUpload = utils.noop,
  onClick = utils.noop,
  disabled = false,
  control,
  validateLink,
}) {
  const rules = {
    validate: validateLink,
  };
  const { reset, getValues, setValue } = useFormContext();
  const { linkType, reference } = getValues();
  const [title, nofollowControl, targetBlank, customPath, customPathSwitcher, download] = useWatch({
    control,
    name: [
      FIELD_NAMES.title,
      FIELD_NAMES.nofollowControl,
      FIELD_NAMES.targetBlank,
      FIELD_NAMES.customPath,
      FIELD_NAMES.customPathSwitcher,
      FIELD_NAMES.download,
    ],
  });

  const { pagesOptions, blocksOptions, linkTypeOptions } = options;
  const { blocksCaptions, pagesCaptions, fileUploadCaptions } = captions;
  const currentLocale = siteLocales.accessors.getCurrentLocale();
  const activePageId = site.accessors.getActivePageId();

  const handleChange = useCallback((newValue) => onChange(newValue), [onChange]);

  const handleClick = (inputId) => () => {
    onClick(inputId);
  };

  const getInnerOptionsByLinkType = (type) => {
    switch (type) {
      case linkTypes.ANCHOR:
        return blocksOptions;
      case linkTypes.PAGE:
        return pagesOptions;
      default:
        return [];
    }
  };

  const getAdditionalInputGroupProps = (type) => {
    switch (type) {
      case linkTypes.PHONE:
        return {
          onClick: handleClick(INPUTS_IDS.phoneInput),
          placeholder: '+1-234-5678910',
        };
      case linkTypes.MAIL:
        return {
          onClick: handleClick(INPUTS_IDS.emailInput),
          placeholder: 'example@email.com',
        };
      case linkTypes.EXTERNAL:
        return {
          disabled,
          onClick: handleClick(INPUTS_IDS.linkInput),
          placeholder: 'https://example.com',
        };
      default:
        return {};
    }
  };

  const innerOptions = getInnerOptionsByLinkType(linkType);
  const additionalInputGroupProps = getAdditionalInputGroupProps(linkType);
  const currentPageId = getInnerOptionsByLinkType(linkType)[0]?.pageId;
  const selectedPageIdRef = useRef(currentPageId || activePageId);

  const onUpload = (fileSet) => {
    onFileUpload(fileSet[0]);
  };

  const onSelectActionType = (option) => {
    const { value: selectedActionType } = option;
    const selectedOptions = getInnerOptionsByLinkType(selectedActionType);
    let selectedReference = '';

    onClick(INPUTS_IDS.actionTypeInput, selectedActionType);

    if (selectedActionType === linkTypes.PAGE) {
      selectedReference = selectedOptions.find((page) => page.locale === currentLocale).value;
    } else if (selectedActionType === linkTypes.ANCHOR && Boolean(latestAnchorReference)) {
      selectedReference = latestAnchorReference;
      selectedPageIdRef.current = findOptionByValue(latestAnchorReference, selectedOptions).pageId;
    } else if (selectedActionType === linkTypes.ANCHOR) {
      selectedPageIdRef.current = selectedOptions[0].pageId || selectedOptions[1].options[0].pageId;
    }

    reset({
      [FIELD_NAMES.reference]: selectedReference,
      [FIELD_NAMES.linkType]: selectedActionType,
      [FIELD_NAMES.title]: title,
      [FIELD_NAMES.nofollowControl]: nofollowControl,
      [FIELD_NAMES.targetBlank]: targetBlank,
      [FIELD_NAMES.customPath]: customPath,
      [FIELD_NAMES.customPathSwitcher]: customPathSwitcher,
      [FIELD_NAMES.download]: download,
    });

    handleChange({
      linkType: selectedActionType,
      reference: selectedReference,
      pageId: selectedPageIdRef.current,
    });
  };

  const onSelectPage = (option) => {
    handleChange({
      linkType,
      reference: option,
    });
  };

  const onSelectAnchor = (option) => {
    if (blocksOptions[0].options) {
      const selectedOption = blocksOptions[0].options.find(({ value }) => value === option);

      if (selectedOption) {
        selectedPageIdRef.current = selectedOption.pageId;
      }
    }

    handleChange({
      linkType,
      reference: option,
      pageId: selectedPageIdRef.current,
    });
  };

  const onResetUploadFileInput = () => {
    handleChange({
      linkType,
      reference: '',
    });
  };

  const onTypeAction = (value) => {
    handleChange({
      linkType,
      reference: value,
    });
  };

  const onSwitchLanguage = (option) => {
    setValue(FIELD_NAMES.reference, option.value);

    handleChange({
      linkType,
      reference: option.value,
    });
  };

  return (
    <Fragment>
      <Stack gap="spacing-3-xs">
        <SimpleSelectField
          name={FIELD_NAMES.linkType}
          control={control}
          options={linkTypeOptions}
          onChange={onSelectActionType}
        />

        {(() => {
          switch (linkType) {
            case linkTypes.VIEW_FULL_SIZE_IMAGE:
            case linkTypes.NO_ACTION:
              return null;
            case linkTypes.PHONE:
            case linkTypes.MAIL:
            case linkTypes.EXTERNAL:
              return (
                <div>
                  <InputGroupField
                    name={FIELD_NAMES.reference}
                    control={control}
                    rules={rules}
                    onChange={onTypeAction}
                    {...additionalInputGroupProps}
                  />
                </div>
              );
            case linkTypes.DOWNLOAD:
              return (
                <FilePickerField
                  name={FIELD_NAMES.reference}
                  control={control}
                  rules={rules}
                  onChange={onUpload}
                  onClick={handleClick(INPUTS_IDS.downloadInput)}
                  label={fileUploadCaptions.button}
                  fileItemName={fileUploadCaptions.fileItemName}
                  placeholder={fileUploadCaptions.empty}
                  onClear={onResetUploadFileInput}
                />
              );

            case linkTypes.PAGE:
              return (
                <PageSelector
                  onChange={onSwitchLanguage}
                  reference={reference}
                  onSelectActionValue={onSelectPage}
                  options={innerOptions}
                  captions={pagesCaptions}
                  control={control}
                />
              );
            default:
              return (
                <div>
                  <SearchableSelectField
                    name={FIELD_NAMES.reference}
                    control={control}
                    rules={rules}
                    placeholder={blocksCaptions.placeholder}
                    noResultsText={blocksCaptions.noResultsText}
                    options={innerOptions}
                    onChange={onSelectAnchor}
                  />
                </div>
              );
          }
        })()}
      </Stack>
    </Fragment>
  );
}

LinkSelectorField.propTypes = {
  latestAnchorReference: PropTypes.string.isRequired,
  options: PropTypes.shape({
    pagesOptions: PropTypes.array.isRequired,
    blocksOptions: PropTypes.array.isRequired,
    linkTypeOptions: PropTypes.array.isRequired,
  }).isRequired,
  captions: PropTypes.shape({
    fileUploadCaptions: PropTypes.shape({
      button: PropTypes.string.isRequired,
      empty: PropTypes.string.isRequired,
      fileItemName: PropTypes.string,
    }),
    pagesCaptions: PropTypes.shape({
      noResultsText: PropTypes.string,
    }),
    blocksCaptions: PropTypes.shape({
      placeholder: PropTypes.string,
      noResultsText: PropTypes.string,
    }),
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  onFileUpload: PropTypes.func,
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
  control: PropTypes.shape({}).isRequired,
  validateLink: PropTypes.func.isRequired,
};

export default LinkSelectorField;
